import React from 'react';
import Link from '@mui/material/Link';
// defines the operations available for each type
const operations = {
  stringOps: {
    eq: '=',
    ne: '!=',
    exists: 'exists',
    wildcard: 'contains',
  },
  boolOps: {
    eq: '=',
    ne: '!=',
  },
  optionOps: {
    eq: '=',
    ne: '!=',
    exists: 'exists',
  },
  numberOps: {
    eq: '=',
    ne: '!=',
    gt: '>',
    gte: '>=',
    lt: '<',
    lte: '<=',
  },
  awsdtOps: {
    gte: 'after',
    lte: 'before',
    between: 'between',
  },
};

// human readable labels for each type
const opLabels = {
  exists: 'exists',
  eq: '=',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  ne: '≠',
  wildcard: 'contains',
  between: 'between',
};

const valueFormatters = {
  awsDateTimeFormatter: (awsDT) => {
    // output will be in the format MM/DD/YYYY, HH:MM:SS
    try {
      const value = typeof awsDT === 'object' && awsDT !== null ? awsDT.value : awsDT;
      if (!value) {
        return '';
      }
      const timestamp = new Date(value);
      if (timestamp.toString() === 'Invalid Date') {
        throw new Error('Invalid Date');
      }
      return timestamp.toLocaleString('en-US', { timeZone: 'UTC' });
    } catch (err) {
      return awsDT;
    }
  },
  awsDateTimeFormatterEastern: (awsDT) => {
    // for values saved with an explicit Eastern offset - render back in Eastern instead of UTC
    // used by is_advantage_member_timestamp on clients table
    try {
      const value = typeof awsDT === 'object' && awsDT !== null ? awsDT.value : awsDT;
      if (!value) {
        return '';
      }
      const timestamp = new Date(value);
      if (timestamp.toString() === 'Invalid Date') {
        throw new Error('Invalid Date');
      }
      return timestamp.toLocaleString('en-US', { timeZone: 'America/New_York' });
    } catch (err) {
      return awsDT;
    }
  },
  dateFormatter: (awsDT) => {
    // just the date portion
    try {
      const value = typeof awsDT === 'object' && awsDT !== null ? awsDT.value : awsDT;

      if (!value) {
        return '';
      }
      const timestamp = new Date(value);
      if (timestamp.toString() === 'Invalid Date') {
        throw new Error('Invalid Date');
      }
      const day = timestamp.getUTCDate();
      const month = timestamp.getUTCMonth() + 1; // 0-indexed but we want 1-12
      const year = timestamp.getUTCFullYear();
      return `${month}/${day}/${year}`;
    } catch (err) {
      return '';
    }
  },
  dateFormatterAWSDate: (params) => {
    try {
      const value = typeof params === 'object' && params !== null ? params.value : params;
      if (!value) {
        return '';
      }
      const [year, month, day] = value.split('-');
      return `${month}/${day}/${year}`;
    } catch (err) {
      return '';
    }
  },
  currencyFormatter: (params) => {
    try {
      const value = typeof params === 'object' && params !== null ? params.value : params;
      if (value === null || value === undefined) {
        return params;
      }
      const formatter = Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });
      return formatter.format(value);
    } catch (err) {
      return params;
    }
  },
  numberFormatter: (params) => {
    try {
      const value = typeof params === 'object' && params !== null ? params.value : params;
      if (value !== null && value !== undefined) {
        return Number(value).toLocaleString('en-US');
      }
      return params.value;
    } catch (err) {
      return '';
    }
  },
  percentFormatter: (params) => {
    try {
      const value = typeof params === 'object' && params !== null ? params.value : params;
      if (value !== null && value !== undefined) {
        return `${Math.round(value * 10) / 10}%`;
      }
      return '';
    } catch (err) {
      return '';
    }
  },
  arrayFormatter: (params) => {
    let value = params;
    if (!Array.isArray(params) && params && typeof params === 'object') {
      value = params.value;
    }
    if (value && value.length > 0) {
      return `${value}`;
    }
    return '';
  },
};

const clientColumns = [
  {
    field: 'code',
    headerName: 'Client',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'jiraname',
    headerName: 'Jira Project',
  },
  {
    field: 'fullname',
    headerName: 'Full Name',
    minWidth: 130,
    flex: 1,
  },
  {
    field: 'clienttype',
    headerName: 'Client Type',
  },
  {
    field: 'adaimember',
    headerName: 'TDC Member',
    minWidth: 110,
  },
  {
    field: 'adsconfiguration',
    headerName: 'Ads Configuration',
  },
  {
    field: 'actblueids',
    headerName: 'Actblue IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'actionsdatasource',
    headerName: 'Actions Data Source',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'adaisuppresseddomains',
    headerName: 'AdAI Suppressed Domains',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'bingids',
    headerName: 'Bing Data Ids',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'campaignmonitorlocation',
    headerName: 'Campaign Monitor Location',
  },
  {
    field: 'classyorgid',
    headerName: "Classy Org ID's",
  },
  {
    field: 'codedicurl',
    headerName: 'Code Dictionary URL',
  },
  {
    field: 'curateautoupload',
    headerName: 'Curate Auto Upload',
  },
  {
    field: 'curatedays',
    headerName: 'Curate Days',
  },
  {
    field: 'curatefrequency',
    headerName: 'Curate Frequency',
  },
  {
    field: 'curatesubscriber',
    headerName: 'Curate Subscriber',
  },
  {
    field: 'emailconfigurations',
    headerName: 'Email Configurations',
  },
  {
    field: 'emailexhaustdatasource',
    headerName: 'Email Exhaust Data Source',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'emailvalidationprovider',
    headerName: 'Email Validation Provider',
  },
  {
    field: 'excludematchqualifier',
    headerName: 'Exclude from Match Qualifier',
  },
  {
    field: 'expandedwindow',
    headerName: 'Expanded Window',
  },
  {
    field: 'facebookids',
    headerName: 'Facebook IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'facebookloc',
    headerName: 'Facebook Location',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'featurize',
    headerName: 'Featurize',
  },
  {
    field: 'fiscalyearstart',
    headerName: 'Fiscal Year Start',
  },
  {
    field: 'googleids',
    headerName: 'Google IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'goopid',
    headerName: 'Goop IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'hubspotstitchintegrationname',
    headerName: 'Hubspot Stitch Integration Name',
  },
  {
    field: 'hubsrcpath',
    headerName: 'Hub Source Path',
  },
  {
    field: 'inboxmonsterrule',
    headerName: 'Inbox Monster Rule',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'isactive',
    headerName: 'Active',
  },
  {
    field: 'isadsreportingactive',
    headerName: 'Ads Reporting',
  },
  {
    field: 'isadvantagemembertimestamp',
    headerName: 'AdAi Member Timestamp',
    valueFormatter: valueFormatters.awsDateTimeFormatterEastern,
  },
  {
    field: 'identity',
    headerName: 'Identity',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'islowinventory',
    headerName: 'Low Inventory',
  },
  {
    field: 'ismidlevelmember',
    headerName: 'Midlevel Member',
  },
  {
    field: 'isstrategypartner',
    headerName: 'Strategy Partner',
  },
  {
    field: 'mailchimpdir',
    headerName: 'Mailchimp Directory',
  },
  {
    field: 'mmclients',
    headerName: 'Multi-Member Email Clients',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'mmrequired',
    headerName: 'Multi-Member Email Required',
  },
  {
    field: 'mmsmsclients',
    headerName: 'Multi-Member SMS Clients',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'mmmlclients',
    headerName: 'Multi-Member Mid-Level Clients',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'mmmlrequired',
    headerName: 'Multi-Member Mid-Level Required',
  },
  {
    field: 'ngpactbluemanualcodes',
    headerName: 'NGP Actblue Manual Codes',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'ngpeadb',
    headerName: 'NGP EA Database',
  },
  {
    field: 'ngpeaserver',
    headerName: 'NGP EA Server',
  },
  {
    field: 'notes',
    headerName: 'Notes',
  },
  {
    field: 'numeroaccountids',
    headerName: 'Numero Account IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'outbrainids',
    headerName: 'Outbrain IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'pardotloc',
    headerName: 'Pardot Location',
  },
  {
    field: 'pausedata',
    headerName: 'Pause Data',
  },
  {
    field: 'peopledatasource',
    headerName: 'People Data Source',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'reactivationconfigurations',
    headerName: 'Reactivation Configurations',
  },
  {
    field: 'salesforceloc',
    headerName: 'Salesforce Location',
  },
  {
    field: 'salsakey',
    headerName: 'Salsa Key',
  },
  {
    field: 'sector',
    headerName: 'Sector',
  },
  {
    field: 'sfgid',
    headerName: 'SFG IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'sharecapmaxshares',
    headerName: 'Share Cap Max Shares',
  },
  {
    field: 'sharecapmonths',
    headerName: 'Share Cap Months',
  },
  {
    field: 'slackchannel',
    headerName: 'Slack Channel',
  },
  {
    field: 'smsconfigurations',
    headerName: 'Sms Configurations',
  },
  {
    field: 'smsshortcodedatasource',
    headerName: 'SMS Shortcode Data Source',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'smslongcodedatasource',
    headerName: 'SMS Longcode Data Source',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'stwaccountids',
    headerName: 'STW Subaccount IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'stwnoncoopsubaccountids',
    headerName: 'STW Non Co-op Subaccount IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'switchboardshortcodedataset',
    headerName: 'Switchboard Shortcode Dataset Name',
  },
  {
    field: 'switchboardlongcodedataset',
    headerName: 'Switchboard Longcode Dataset Name',
  },
  {
    field: 'tatangoids',
    headerName: 'Tatango IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'timezone',
    headerName: 'Timezone',
  },
  {
    field: 'totemid',
    headerName: 'Totem IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'transactionsdatasource',
    headerName: 'Transactions Data Source',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'transactionsofflinedatasource',
    headerName: 'Transactions Offline Data Source',
  },
  {
    field: 'multichanneltransactionsdatasource',
    headerName: 'Multichannel Transactions Data Source',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'twitterids',
    headerName: 'Twitter IDs',
    formatterName: 'arrayFormatter',
    valueFormatter: valueFormatters.arrayFormatter,
  },
  {
    field: 'vendor',
    headerName: 'Vendor',
  },
  {
    field: 'updatedat',
    headerName: 'Updated At',
    format: 'AWSDate',
    valueFormatter: valueFormatters.dateFormatter,
    formatterName: 'dateFormatter',
  },
  {
    field: 'createdat',
    headerName: 'Created At',
    format: 'AWSDate',
    valueFormatter: valueFormatters.dateFormatter,
    formatterName: 'dateFormatter',
  },
];

// used for sources/codes records and searching sources/codes
// see also enum channelcategory under graphql schema
const channelCategoryEnum = [
  'Ads',
  'Bundling',
  'Display',
  'Email',
  'Experimental',
  'Fixed_Cost',
  'Native',
  'Organic_Social',
  'Other',
  'Search',
  'SMS_Shortcode',
  'SMS_Longcode',
  'Social',
  'Substack',
  'Swap',
  'Tapjoy',
  'Video',
  'Web',
  'Youtube',
];

const codeColumns = [
  {
    field: 'client_id',
    headerName: 'Client',
    format: 'String',
    sortable: false,
  },
  {
    field: 'code',
    headerName: 'Code',
    width: 200,
    format: 'String',
    sortable: false,
  },
  {
    field: 'send_cost',
    headerName: 'Send Cost',
    type: 'number',
    format: 'Float',
    width: 100,
    valueFormatter: valueFormatters.currencyFormatter,
    formatterName: 'currencyFormatter',
    sortable: false,
    editable: true,
    renderEditCell: (params) => (
      <input
        type="number"
        min="0"
        style={{
          width: '100%', height: '100%', border: 'none', outline: 'none',
        }}
        value={params.value ?? ''}
        // Block the minus key (-) from being typed
        onKeyDown={(e) => {
          if (e.key === '-') {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          let newValue;
          const isANumber = !Number.isNaN(Number(e.target.value));
          if (isANumber) {
            newValue = parseFloat(e.target.value);
          } else if (e.target.value === '') {
            newValue = '';
          }
          // Prevent negative values from pasted input or edge cases
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: (newValue >= 0 || newValue === '') ? newValue : '',
          });
        }}
      />
    ),
  },
  {
    field: 'send_size',
    headerName: 'Send Size',
    format: 'Int',
    width: 100,
    valueFormatter: valueFormatters.numberFormatter,
    formatterName: 'numberFormatter',
    sortable: false,
    editable: true,
    renderEditCell: (params) => (
      <input
        type="number"
        min="0"
        style={{
          width: '100%', height: '100%', border: 'none', outline: 'none',
        }}
        value={params.value ?? ''}
        // Block the minus key (-) from being typed
        onKeyDown={(e) => {
          if (e.key === '-') {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          let newValue;
          const isANumber = !Number.isNaN(Number(e.target.value));
          if (isANumber) {
            newValue = parseFloat(e.target.value);
          } else if (e.target.value === '') {
            newValue = '';
          }
          // Prevent negative values from pasted input or edge cases
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: (newValue >= 0 || newValue === '') ? newValue : '',
          });
        }}
      />
    ),
  },
  {
    field: 'version',
    headerName: 'Version',
    width: 100,
    format: 'String',
    sortable: false,
    editable: true,
  },
  {
    field: 'jira_ticket_num',
    headerName: 'Jira Ticket',
    width: 100,
    format: 'String',
    sortable: false,
    editable: true,
  },
  {
    field: 'acquisitions',
    headerName: 'Acquisitions',
    width: 150,
    format: 'String',
    sortable: false,
  },
  {
    field: 'audience',
    headerName: 'Audience',
    width: 150,
    format: 'String',
    sortable: false,
    editable: true,
  },
  {
    field: 'audience_category',
    headerName: 'Audience Category',
    width: 150,
    format: 'String',
    sortable: false,
    editable: true,
  },
  {
    field: 'revenue',
    headerName: 'Revenue',
    width: 120,
    format: 'Float',
    type: 'number',
    valueFormatter: valueFormatters.currencyFormatter,
    formatterName: 'currencyFormatter',
    sortable: false,
  },
  {
    field: 'revenue_category',
    headerName: 'Revenue Category',
    format: 'String',
    sortable: false,
    editable: true,
  },
  {
    field: 'type',
    headerName: 'Revenue Type',
    width: 135,
    format: 'String',
    sortable: false,
    editable: true,
  },
  {
    field: 'fixed_cost_cpa_override',
    headerName: 'Fixed Cost CPA',
    format: 'Float',
    type: 'number',
    valueFormatter: valueFormatters.currencyFormatter,
    formatterName: 'currencyFormatter',
    sortable: false,
    editable: true,
    renderEditCell: (params) => (
      <input
        type="number"
        min="0"
        style={{
          width: '100%', height: '100%', border: 'none', outline: 'none',
        }}
        value={params.value ?? ''}
        // Block the minus key (-) from being typed
        onKeyDown={(e) => {
          if (e.key === '-') {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          let newValue;
          const isANumber = !Number.isNaN(Number(e.target.value));
          if (isANumber) {
            newValue = parseFloat(e.target.value);
          } else if (e.target.value === '') {
            newValue = '';
          }
          // Prevent negative values from pasted input or edge cases
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: (newValue >= 0 || newValue === '') ? newValue : '',
          });
        }}
      />
    ),
  },
  {
    field: 'fixed_cost_media_spend',
    headerName: 'Fixed Cost Media Spend',
    format: 'Float',
    type: 'number',
    valueFormatter: valueFormatters.currencyFormatter,
    formatterName: 'currencyFormatter',
    sortable: false,
    editable: true,
    renderEditCell: (params) => (
      <input
        type="number"
        min="0"
        style={{
          width: '100%', height: '100%', border: 'none', outline: 'none',
        }}
        value={params.value ?? ''}
        // Block the minus key (-) from being typed
        onKeyDown={(e) => {
          if (e.key === '-') {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          let newValue;
          const isANumber = !Number.isNaN(Number(e.target.value));
          if (isANumber) {
            newValue = parseFloat(e.target.value);
          } else if (e.target.value === '') {
            newValue = '';
          }
          // Prevent negative values from pasted input or edge cases
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: (newValue >= 0 || newValue === '') ? newValue : '',
          });
        }}
      />
    ),
  },
  {
    field: 'most_recent_spend',
    headerName: 'Most Recent Spend',
    format: 'Float',
    type: 'number',
    valueFormatter: valueFormatters.currencyFormatter,
    formatterName: 'currencyFormatter',
    sortable: false,
  },
  {
    field: 'most_recent_spend_date',
    headerName: 'Most Recent Spend Date',
    format: 'AWSDate',
    valueFormatter: valueFormatters.dateFormatter,
    formatterName: 'dateFormatter',
    sortable: false,
  },
  {
    field: 'platforms_with_spend',
    headerName: 'Platforms With Spend',
    options: ['facebook', 'google', 'twitter', 'bing'],
    format: 'options',
    sortable: false,
  },
  {
    field: 'total_spend',
    headerName: 'Total Spend',
    format: 'Float',
    type: 'number',
    valueFormatter: valueFormatters.currencyFormatter,
    formatterName: 'currencyFormatter',
    sortable: false,
  },
  {
    field: 'transactions',
    headerName: 'Transactions',
    format: 'Int',
    valueFormatter: valueFormatters.numberFormatter,
    formatterName: 'numberFormatter',
    sortable: false,
  },
  {
    field: 'most_recent_transaction_date',
    headerName: 'Most Recent Transaction Date',
    format: 'AWSDate',
    valueFormatter: valueFormatters.dateFormatter,
    formatterName: 'dateFormatter',
    sortable: false,
  },
  {
    field: 'page',
    headerName: 'Page(s)',
    format: 'String',
    sortable: false,
  },
  {
    field: 'actblue_managing_entities',
    headerName: 'Actblue Managing Entities',
    format: 'String',
    sortable: false,
    // potentially we will want to have these listed on multiple lines, like a list
  },
  {
    field: 'from_goop',
    headerName: 'From Goop',
    format: 'Boolean',
    sortable: false,
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    format: 'AWSDate',
    valueFormatter: valueFormatters.dateFormatter,
    formatterName: 'dateFormatter',
    sortable: false,
  },
];

const sourceColumns = [
  {
    field: 'client_id',
    headerName: 'Client',
    flex: 1,
    format: 'String',
    sortable: false,
  },
  {
    field: 'source',
    headerName: 'Source',
    width: 400,
    format: 'String',
    sortable: false,
  },
  {
    field: 'channel_category',
    headerName: 'Channel Category',
    flex: 1,
    options: channelCategoryEnum,
    format: 'options',
    sortable: false,
  },
  {
    field: 'from_goop',
    headerName: 'From Goop',
    format: 'Boolean',
    sortable: false,
  },
  {
    field: 'send_date',
    headerName: 'Send Date',
    format: 'AWSDate',
    flex: 1,
    valueFormatter: valueFormatters.dateFormatterAWSDate,
    formatterName: 'dateFormatterAWSDate',
    sortable: false,
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    format: 'AWSDate',
    flex: 1,
    valueFormatter: valueFormatters.dateFormatter,
    formatterName: 'dateFormatter',
    sortable: false,
  },
];

const transactionColumns = [
  {
    field: 'client_id',
    headerName: 'Client',
    format: 'String',
    sortable: false,
  },
  {
    field: 'email',
    headerName: 'Email',
    format: 'String',
    sortable: false,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    type: 'number',
    format: 'Float',
    valueFormatter: valueFormatters.currencyFormatter,
    formatterName: 'currencyFormatter',
    sortable: false,
  },
  {
    field: 'exclude_flag',
    headerName: 'Excluded',
    format: 'Boolean',
    sortable: false,
  },
  {
    field: 'code',
    headerName: 'Code',
    width: 300,
    format: 'String',
    sortable: false,
  },
  {
    field: 'code_source',
    headerName: 'Source',
    width: 300,
    format: 'String',
    sortable: false,
    excludeFromSearch: true,
  },
  {
    field: 'foreign_platform',
    headerName: 'Transaction Platform',
    format: 'String',
    sortable: false,
  },
  {
    field: 'foreign_client_key',
    headerName: 'Client Platform ID',
    format: 'String',
    sortable: false,
  },
  {
    field: 'foreign_transaction_key',
    headerName: 'Transaction ID',
    format: 'String',
    sortable: false,
  },
  {
    field: 'recurrence',
    headerName: 'Recurrence Number',
    format: 'Int',
    sortable: false,
  },
  {
    field: 'recurring_period',
    headerName: 'Recurring Period',
    format: 'String',
    sortable: false,
  },
  {
    field: 'recurring_type',
    headerName: 'Recurring Type',
    format: 'String',
    sortable: false,
  },
  {
    field: 'transaction_dt',
    headerName: 'Transaction Date',
    format: 'AWSDate',
    formatterName: 'dateFormatter',
    valueFormatter: valueFormatters.dateFormatter,
    sortable: false,
  },
  {
    field: 'refund_flag',
    headerName: 'Refunded',
    format: 'Boolean',
    sortable: false,
  },
  {
    field: 'updatedAt',
    headerName: 'Updated At',
    format: 'AWSDate',
    valueFormatter: valueFormatters.dateFormatter,
    formatterName: 'dateFormatter',
    sortable: false,
  },
  {
    field: 'mobile_flag',
    headerName: 'Mobile Flag',
    format: 'Boolean',
    sortable: false,
  },
];

const insightsColumns = [
  // pinned
  {
    field: 'updated_dt',
    headerName: 'updated_dt',
    format: 'AWSDateTime',
    formatterName: 'awsDateTimeFormatter',
    valueFormatter: valueFormatters.awsDateTimeFormatter,
    minWidth: 160,
  },
  {
    field: 'client_type',
    headerName: 'client_type',
    format: 'String',
    width: 85,
  },
  {
    field: 'client',
    headerName: 'client',
    format: 'String',
  },
  {
    field: 'jira_ticket_num',
    headerName: 'jira_ticket_num',
    format: 'String',
    minWidth: 75,
  },
  {
    field: 'first_delivery_date',
    headerName: 'first_delivery_date',
    format: 'AWSDate',
    formatterName: 'dateFormatter',
    valueFormatter: valueFormatters.dateFormatter,
    minWidth: 160,
  },
  {
    field: 'delivery_month',
    headerName: 'Delivery Month',
    format: 'Int',
  },
  {
    field: 'delivery_year',
    headerName: 'Delivery Year',
    format: 'Int',
  },
  // not pinned
  {
    field: 'first_uploaded_date',
    headerName: 'first_uploaded_date',
    format: 'AWSDate',
    formatterName: 'dateFormatter',
    valueFormatter: valueFormatters.dateFormatter,
    minWidth: 160,
  },
  {
    field: 'emails_acquired',
    headerName: 'emails_acquired',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
    format: 'Int',
  },
  {
    field: 'spend',
    headerName: 'spend',
    format: 'Float',
    type: 'number',
    formatterName: 'currencyFormatter',
    valueFormatter: valueFormatters.currencyFormatter,
  },
  {
    field: 'revenue',
    headerName: 'revenue',
    format: 'Float',
    type: 'number',
    formatterName: 'currencyFormatter',
    valueFormatter: valueFormatters.currencyFormatter,
  },
  {
    field: 'ROAS',
    headerName: 'ROAS',
    format: 'Float',
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'sends',
    headerName: 'sends',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'pixel_loads',
    headerName: 'pixel_loads',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'pixel_load_rate',
    headerName: 'pixel_load_rate (%)',
    format: 'Float',
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'opens',
    headerName: 'opens',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'open_rate',
    headerName: 'open_rate',
    format: 'Float',
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'clicks',
    headerName: 'clicks',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'click_rate',
    headerName: 'click_rate',
    format: 'Float',
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'Unsubs',
    headerName: 'Unsubs',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'donors',
    headerName: 'donors',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'contributions',
    headerName: 'contributions',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'average_gift',
    headerName: 'average_gift',
    format: 'Float',
    type: 'number',
    formatterName: 'currencyFormatter',
    valueFormatter: valueFormatters.currencyFormatter,
  },
  {
    field: 'ROAS_Over_Days',
    headerName: 'ROAS_Over_Days',
    format: 'Float',
    minWidth: 150,
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'Days_to_break_even',
    headerName: 'Days_to_break_even',
    format: 'Int',
    minWidth: 150,
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'Break_even_date',
    headerName: 'Break_even_date',
    format: 'AWSDateTime',
    formatterName: 'dateFormatter',
    valueFormatter: valueFormatters.dateFormatter,
    minWidth: 160,
  },
  {
    field: 'Projected_days_to_break_even',
    headerName: 'Projected_days_to_break_even',
    format: 'Int',
    minWidth: 175,
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'Projected_break_even_date',
    headerName: 'Projected_break_even_date',
    format: 'AWSDateTime',
    formatterName: 'dateFormatter',
    valueFormatter: valueFormatters.dateFormatter,
    minWidth: 160,
  },
  {
    field: 'gifts_over_opens',
    headerName: 'gifts_over_opens (%)',
    format: 'Float',
    minWidth: 125,
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'contributions_over_sends',
    headerName: 'contributions_over_sends',
    format: 'Float',
    minWidth: 175,
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'emails_messaged',
    headerName: 'emails_messaged',
    format: 'Int',
    minWidth: 75,
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'pixel_load_emails',
    headerName: 'pixel_load_emails',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'open_emails',
    headerName: 'open_emails',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'click_emails',
    headerName: 'click_emails',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'optouts',
    headerName: 'optouts',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'optout_emails',
    headerName: 'optout_emails',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'optout_rate',
    headerName: 'optout_rate',
    format: 'Float',
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'hard_bounces',
    headerName: 'hard_bounces',
    format: 'Int',
    minWidth: 75,
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'hard_bounce_emails',
    headerName: 'hard_bounce_emails',
    format: 'Int',
    minWidth: 100,
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'hard_bounce_rate',
    headerName: 'hard_bounce_rate',
    format: 'Float',
    minWidth: 75,
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'soft_bounces',
    headerName: 'soft_bounces',
    format: 'Int',
    minWidth: 75,
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'soft_bounce_emails',
    headerName: 'soft_bounce_emails',
    format: 'Int',
    minWidth: 75,
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'soft_bounce_rate',
    headerName: 'soft_bounce_rate',
    format: 'Float',
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'spams',
    headerName: 'spams',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'spam_emails',
    headerName: 'spam_emails',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'spam_rate',
    headerName: 'spam_rate',
    format: 'Float',
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
  },
  {
    field: 'send_emails_past_30',
    headerName: 'send_emails_past_30',
    format: 'Int',
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
  },
  {
    field: 'cost_per_donor',
    headerName: 'cost_per_donor',
    format: 'Float',
    formatterName: 'currencyFormatter',
    valueFormatter: valueFormatters.currencyFormatter,
  },
];

const cohortColumns = [
  {
    field: 'cohort',
    headerName: 'cohort',
    format: 'String',
  },
  {
    field: 'cohort_name',
    headerName: 'cohort_name',
    format: 'String',
  },
];

const reacCohortColumn = [
  {
    field: 'recurring_flag',
    headerName: 'recurring_flag',
    format: 'String',
  },
];

const insightsNonCohortColumns = insightsColumns;
const insightsCohortColumns = insightsColumns.concat(cohortColumns);
const insightsReacCohortColumns = insightsCohortColumns.concat(reacCohortColumn);

const importHistoryColumns = [
  {
    field: 'client',
    headerName: 'Client',
    format: 'String',
  },
  {
    field: 'csv_name',
    headerName: 'CSV name',
    width: 150,
    format: 'String',
  },
  {
    field: 'timestamp',
    headerName: 'Timestamp',
    format: 'AWSDateTime',
    formatterName: 'awsDateTimeFormatter',
    valueFormatter: valueFormatters.awsDateTimeFormatter,
  },
  {
    field: 'status',
    headerName: 'Status',
    format: 'String',
  },
  // generated in code, not in db
  {
    field: 'count',
    headerName: 'Count',
    width: 120,
    format: 'String',
  },
  {
    field: 'exclusionCount',
    headerName: 'Exclusion count',
    width: 150,
    format: 'String',
  },
  {
    field: 'inclusionCount',
    headerName: 'Inclusion count',
    width: 150,
    format: 'String',
  },
  {
    field: 'error',
    headerName: 'Error',
    flex: 1,
    format: 'String',
    renderCell: (params) => {
      const { signed_url: signedUrl } = params.row;
      return (
        <span>
          {params.value}
          {signedUrl && (
            <>
              {params.value ? ' — ' : ''}
              <Link href={signedUrl} target="_blank" rel="noopener">
                Download Error Report
              </Link>
            </>
          )}
        </span>
      );
    },
  },
  {
    field: 'warning',
    headerName: 'Warning',
    flex: 1,
    format: 'String',
  },
  {
    field: 'createdAt',
    headerName: 'Created at',
    format: 'String',
  },
  {
    field: 'updatedAt',
    headerName: 'Updated at',
    format: 'String',
  },
];

const sumBySourceExportResultsColumns = [
  {
    field: 'id',
    headerName: 'ID',
    width: 25,
    format: 'String',
    sortable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 100,
    options: [
      'Pending',
      'Success',
      'Failed',
    ],
    format: 'options',
    sortable: false,
  },
  {
    field: 'report_type',
    headerName: 'Report Type',
    width: 200,
    flex: 1,
    format: 'String',
    sortable: false,
  },
  {
    field: 'date_range',
    headerName: 'Date Range',
    width: 200,
    flex: 1,
    format: 'String',
    sortable: false,
  },
  {
    field: 'breakdown_period',
    headerName: 'Breakdown Period',
    width: 75,
    flex: 1,
    format: 'String',
    sortable: false,
  },
  {
    field: 'signed_s3_url',
    headerName: 'Download Link',
    width: 150,
    renderCell: (params) => (
      <strong>
        <Link href={params.value} target="_blank" rel="noopener">
          Download Report
        </Link>
      </strong>
    ),
  },
  {
    field: 'saved_dt',
    headerName: 'Saved Date',
    width: 150,
    flex: 1,
    format: 'AWSDateTime',
    formatterName: 'awsDateTimeFormatter',
    valueFormatter: valueFormatters.awsDateTimeFormatter,
  },
  {
    field: 'errors',
    headerName: 'Errors',
    width: 150,
    format: 'String',
    sortable: false,
  },
];

const sumBySourceMonthlyResultsColumns = [
  {
    field: 'channel_category',
    headerName: 'Channel Category',
    flex: 1,
    format: 'String',
    sortable: false,
    resizable: false,
  },
  {
    field: 'gift_count',
    headerName: 'Gift Count',
    format: 'Int',
    flex: 1,
    formatterName: 'numberFormatter',
    valueFormatter: valueFormatters.numberFormatter,
    sortable: true,
    resizable: false,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    type: 'number',
    format: 'Float',
    flex: 1,
    valueFormatter: valueFormatters.currencyFormatter,
    formatterName: 'currencyFormatter',
    sortable: true,
    resizable: false,
  },
  {
    field: 'percent_of_total',
    headerName: '% of Total',
    flex: 1,
    format: 'Float',
    sortable: true,
    formatterName: 'percentFormatter',
    valueFormatter: valueFormatters.percentFormatter,
    resizable: false,
  },
];

export {
  operations,
  opLabels,
  valueFormatters,
  clientColumns,
  codeColumns,
  sourceColumns,
  transactionColumns,
  insightsNonCohortColumns,
  insightsCohortColumns,
  insightsReacCohortColumns,
  importHistoryColumns,
  channelCategoryEnum,
  sumBySourceExportResultsColumns,
  sumBySourceMonthlyResultsColumns,
};
